import { useMemo, useState } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Textarea } from "../../components/ui/textarea";
import { StatusTimeline } from "./StatusTimeline";
import { LenderTag } from "../../types/deals";
import { Building2, Edit2, Check, X, FileText, ShieldCheck, Upload, Eye, RefreshCw, Plus } from "lucide-react";
import { getStatusConfig } from "../../utils/statusConfig";
import { parseIdbUrl } from "../../utils/idbFiles";
import { useIdbObjectUrl } from "../../hooks/use-idb-object-url";

interface LenderCardProps {
  lender: LenderTag;
  onUpdateStatus: () => void;
  onNoteUpdate: (note: string) => void;
  onSanctionLetterUpload?: (file: File) => void;
  onCollateralUpdate?: (details: string) => void;
}

export function LenderCard({ lender, onUpdateStatus, onNoteUpdate, onSanctionLetterUpload, onCollateralUpdate }: LenderCardProps) {
  const [editingNote, setEditingNote] = useState(false);
  const [noteValue, setNoteValue] = useState(lender.note || "");
  const [editingCollateral, setEditingCollateral] = useState(false);
  const [collateralValue, setCollateralValue] = useState("");

  const handleSaveNote = () => {
    onNoteUpdate(noteValue);
    setEditingNote(false);
  };

  const handleCancelNote = () => {
    setNoteValue(lender.note || "");
    setEditingNote(false);
  };

  const handleSaveCollateral = () => {
    onCollateralUpdate?.(collateralValue);
    setEditingCollateral(false);
  };

  // Get latest financial data
  const lastSanction = [...lender.statusHistory].reverse().find(e => typeof e.sanctionAmount === 'number')?.sanctionAmount;
  const lastDisbursedEntry = [...lender.statusHistory].reverse().find(e => typeof e.disbursedAmount === 'number');
  const lastDisbursed = lastDisbursedEntry?.disbursedAmount;
  const latestStatus = lender.statusHistory[lender.statusHistory.length - 1]?.status;
  const payoutCustomer = lastDisbursedEntry?.customerPayout;
  const payoutLender = lastDisbursedEntry?.lenderPayout;
  const payoutPartner = lastDisbursedEntry?.partnerPayout;

  const lastSanctionEntry = useMemo(
    () => [...lender.statusHistory].reverse().find((e) => e.sanctionDate || e.collateralDetails || e.sanctionLetterUrl || e.sanctionLetterName),
    [lender.statusHistory]
  );
  const sanctionDate = lastSanctionEntry?.sanctionDate;
  const collateralDetails = lastSanctionEntry?.collateralDetails;
  const sanctionLetter = lastSanctionEntry?.sanctionLetterUrl
    ? { name: lastSanctionEntry?.sanctionLetterName || "Sanction Letter", url: lastSanctionEntry?.sanctionLetterUrl }
    : null;

  const sanctionLetterIdbKey = useMemo(() => parseIdbUrl(sanctionLetter?.url), [sanctionLetter?.url]);
  const { url: sanctionLetterViewUrl } = useIdbObjectUrl(sanctionLetterIdbKey);
  const resolvedSanctionLetterUrl = sanctionLetterViewUrl ?? (sanctionLetter?.url ?? null);

  const statusConfig = getStatusConfig(latestStatus);
  const StatusIcon = statusConfig?.icon;

  return (
    <Card className="border">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5 text-primary" />
            <div>
              <div className="font-medium">{lender.institution}</div>
              <div className="text-sm text-muted-foreground">{lender.lenderName}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={onUpdateStatus}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Update Status
            </Button>
          </div>
        </div>

        {/* Basic Info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
          <div>
            <div className="text-muted-foreground">Tagged On</div>
            <div>{new Date(lender.taggedAt).toLocaleDateString("en-GB")}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Sanction Date</div>
            <div>{sanctionDate ? new Date(sanctionDate).toLocaleDateString("en-GB") : '-'}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Status</div>
            <div className="flex items-center gap-2">
              {StatusIcon && <StatusIcon className={`h-4 w-4 ${statusConfig?.color || 'text-gray-600'}`} />}
              <Badge variant={statusConfig?.variant || 'secondary'}>
                {latestStatus ?? 'Not Set'}
              </Badge>
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">RM</div>
            <div>{lender.applicationRM ?? '-'}</div>
          </div>
        </div>

        {/* Sanction Letter - Full width row */}
        <div className="rounded-md border p-3 mb-3 text-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Sanction Letter</span>
            </div>
            <div className="flex items-center gap-1">
              {sanctionLetter && (
                <>
                  <div className="flex items-center gap-2 mr-2 px-2 py-1 rounded bg-muted/50">
                    <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-xs truncate max-w-[160px]">{sanctionLetter.name}</span>
                  </div>
                  <a href={resolvedSanctionLetterUrl ?? undefined} target="_blank" rel="noreferrer">
                    <Button size="icon" variant="ghost" className="h-7 w-7" title="View">
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  </a>
                </>
              )}
              {onSanctionLetterUpload && (
                <div className="relative">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) onSanctionLetterUpload(file);
                      e.currentTarget.value = "";
                    }}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    aria-label={sanctionLetter ? "Replace sanction letter" : "Upload sanction letter"}
                  />
                  <Button size="icon" variant="ghost" className="h-7 w-7" title={sanctionLetter ? "Replace" : "Upload"}>
                    <Upload className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
              {!sanctionLetter && !onSanctionLetterUpload && (
                <span className="text-xs text-muted-foreground">Not uploaded</span>
              )}
            </div>
          </div>
        </div>

        {/* Collateral / Security Details - Full width row, note-style */}
        <div className="rounded-md border p-3 mb-4 text-sm">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Collateral / Security Details</span>
            </div>
            {!editingCollateral && (
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditingCollateral(true); setCollateralValue(collateralDetails || ""); }}>
                {collateralDetails ? <Edit2 className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
              </Button>
            )}
          </div>
          {editingCollateral ? (
            <div className="space-y-2">
              <Textarea
                value={collateralValue}
                onChange={(e) => setCollateralValue(e.target.value)}
                placeholder="Enter collateral or security details..."
                className="text-sm"
                rows={3}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveCollateral}>
                  <Check className="h-3 w-3 mr-1" /> Save
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setEditingCollateral(false)}>
                  <X className="h-3 w-3 mr-1" /> Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground whitespace-pre-wrap">
              {collateralDetails || "No collateral details added"}
            </div>
          )}
        </div>

        {/* Financial Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="rounded-md border p-3">
            <div className="text-sm font-medium mb-2">Sanction & Disbursement</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-muted-foreground">Sanctioned</div>
              <div>{typeof lastSanction === 'number' ? new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(lastSanction) : '-'}</div>
              <div className="text-muted-foreground">Disbursed</div>
              <div>{typeof lastDisbursed === 'number' ? new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(lastDisbursed) : '-'}</div>
            </div>
          </div>
          <div className="rounded-md border p-3">
            <div className="text-sm font-medium mb-2">Payout Details</div>
            <div className="space-y-1 text-xs">
              <div>Customer: {payoutCustomer ? new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(payoutCustomer) : '-'}</div>
              <div>Lender: {payoutLender ? new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(payoutLender) : '-'}</div>
              <div>Partner: {payoutPartner ? new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(payoutPartner) : '-'}</div>
            </div>
          </div>
        </div>

        {/* Editable Note */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium">Note</div>
            {!editingNote && (
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => setEditingNote(true)}
                className="h-auto p-1"
              >
                <Edit2 className="h-3 w-3" />
              </Button>
            )}
          </div>
          {editingNote ? (
            <div className="space-y-2">
              <Textarea 
                value={noteValue}
                onChange={(e) => setNoteValue(e.target.value)}
                placeholder="Add a note..."
                className="text-sm"
                rows={2}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveNote}>
                  <Check className="h-3 w-3 mr-1" />
                  Save
                </Button>
                <Button size="sm" variant="secondary" onClick={handleCancelNote}>
                  <X className="h-3 w-3 mr-1" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              {lender.note || "No note added"}
            </div>
          )}
        </div>

        {/* Timeline in Accordion */}
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="timeline" className="border-none">
            <AccordionTrigger className="hover:no-underline py-2">
              <div className="text-sm font-medium">Status Timeline ({lender.statusHistory.length} entries)</div>
            </AccordionTrigger>
            <AccordionContent>
              <StatusTimeline entries={lender.statusHistory} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
}